/** Loan and mortgage amortisation with optional extra payment and PMI. Rate is an annual percentage. */
export interface LoanRow { month: number; payment: number; principal: number; interest: number; extra: number; pmi: number; balance: number }
export interface LoanResult {
  payment: number; months: number; rows: LoanRow[]; totalInterest: number; totalPaid: number; payoffMonths: number;
  baseInterest: number; interestSaved: number; monthsSaved: number; pmiMonths: number; totalPmi: number;
}

export function monthlyPayment(principal: number, annualPct: number, months: number): number {
  if (months <= 0 || principal <= 0) return 0;
  const r = annualPct / 100 / 12;
  return r > 0 ? (principal * r) / (1 - Math.pow(1 + r, -months)) : principal / months;
}

export function amortize(opts: { principal: number; annualPct: number; months: number; extra?: number; oneTime?: { month: number; amount: number }; yearlyExtra?: { amount: number; month: number }; pmiMonthly?: number; pmiDropBalance?: number }): LoanResult {
  const months = Math.max(0, Math.round(opts.months));
  const payment = monthlyPayment(opts.principal, opts.annualPct, months);
  const r = opts.annualPct / 100 / 12;
  const extra = Math.max(0, opts.extra ?? 0);
  const rows: LoanRow[] = [];
  let bal = opts.principal, totalInterest = 0, pmiMonths = 0, totalPmi = 0;
  for (let m = 1; m <= months && bal > 0.005; m++) {
    const interest = bal * r;
    let principal = Math.min(bal, payment - interest);
    // A yearly extra lands in payment `month` of the loan and every 12 payments after it.
    const yearly = opts.yearlyExtra && opts.yearlyExtra.month >= 1 && m >= opts.yearlyExtra.month && (m - opts.yearlyExtra.month) % 12 === 0 ? Math.max(0, opts.yearlyExtra.amount) : 0;
    let ex = Math.min(extra + yearly + (opts.oneTime && opts.oneTime.month === m ? Math.max(0, opts.oneTime.amount) : 0), Math.max(0, bal - principal));
    if (m === months) { principal = bal; ex = 0; }
    const pmi = opts.pmiMonthly && opts.pmiMonthly > 0 && bal > (opts.pmiDropBalance ?? 0) ? opts.pmiMonthly : 0;
    if (pmi) { pmiMonths++; totalPmi += pmi; }
    bal = Math.max(0, bal - principal - ex);
    totalInterest += interest;
    rows.push({ month: m, payment: principal + interest, principal, interest, extra: ex, pmi, balance: bal });
  }
  const hasExtras = extra > 0 || (opts.oneTime?.amount ?? 0) > 0 || (opts.yearlyExtra?.amount ?? 0) > 0;
  const base = hasExtras ? amortize({ ...opts, extra: 0, oneTime: undefined, yearlyExtra: undefined }) : null;
  const baseInterest = base ? base.totalInterest : totalInterest;
  return {
    payment, months, rows, totalInterest, totalPaid: opts.principal + totalInterest, payoffMonths: rows.length,
    baseInterest, interestSaved: Math.max(0, baseInterest - totalInterest), monthsSaved: base ? Math.max(0, base.payoffMonths - rows.length) : 0, pmiMonths, totalPmi,
  };
}

/** Flat-rate EMI (common in India and some other markets): interest is charged on the original principal for the whole term. */
export function flatRateLoan(principal: number, annualPct: number, months: number): LoanResult {
  const n = Math.max(1, Math.round(months));
  const totalInterest = principal * (annualPct / 100) * (n / 12);
  const payment = (principal + totalInterest) / n;
  const rows: LoanRow[] = [];
  let bal = principal;
  for (let m = 1; m <= n; m++) {
    const interest = totalInterest / n;
    const pr = principal / n;
    bal = m === n ? 0 : Math.max(0, bal - pr);
    rows.push({ month: m, payment, principal: pr, interest, extra: 0, pmi: 0, balance: bal });
  }
  return { payment, months: n, rows, totalInterest, totalPaid: principal + totalInterest, payoffMonths: n, baseInterest: totalInterest, interestSaved: 0, monthsSaved: 0, pmiMonths: 0, totalPmi: 0 };
}

/** Plain-English "time saved" label, e.g. "2 years 3 months" or "1 month". */
export function formatMonthsSaved(months: number): string {
  const m = Math.max(0, Math.round(months));
  const y = Math.floor(m / 12), r = m % 12;
  const parts: string[] = [];
  if (y) parts.push(`${y} ${y === 1 ? "year" : "years"}`);
  if (r || !y) parts.push(`${r} ${r === 1 ? "month" : "months"}`);
  return parts.join(" ");
}
